/**
 * Brand dashboard — MAP and price monitoring for one brand.
 *
 * The same page serves admins and the brand's own vendor, so every control
 * carries its permission with it. "View as the vendor" switches the page to the
 * vendor's permission set locally, which is how an admin checks what a rep will
 * actually see before turning monitoring on.
 */

import React, { useMemo, useState } from 'react';
import { Alert, Box, CircularProgress } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import {
  brandDirectoryApi,
  BrandMonitoringSettings,
  BrandRetailerPerformance,
} from '../../services/brandDirectoryApi';
import { useImpersonation } from '../../contexts/ImpersonationContext';
import {
  bvCardSx,
  bvColor,
  bvContentSx,
  bvRadius,
  bvTransition,
  bvType,
  formatCount,
  tabular,
} from './brandVendorsTokens';
import { bvRoutes } from './brandVendorsRoutes';
import {
  BvButton,
  BvCard,
  BvSwitch,
  BvEmptyState,
  Eyebrow,
  IconChevron,
  LogoFrame,
  ProgressBar,
  StatusDot,
  Stepper,
} from './components/BrandVendorsPrimitives';

const GRID = 'minmax(140px,1.5fr) minmax(120px,1.1fr) minmax(72px,0.7fr) minmax(84px,0.8fr) 82px';

const COMPLIANCE: Record<
  BrandRetailerPerformance['compliance'],
  { label: string; ink: string; tone: 'ok' | 'part' | 'no'; percent: number }
> = {
  in_policy: { label: 'In policy', ink: bvColor.success, tone: 'ok', percent: 100 },
  drifting: { label: 'Drifting', ink: bvColor.warning, tone: 'part', percent: 72 },
  violating: { label: 'Violating', ink: bvColor.danger, tone: 'no', percent: 38 },
};

/**
 * Monitoring toggles and who may change them. Retailer alerts reach third
 * parties on the brand's behalf, so they stay with admins even in vendor view.
 */
const MONITORING_ROWS: Array<{
  key: keyof BrandMonitoringSettings;
  label: string;
  note: string;
  adminOnly: boolean;
}> = [
  {
    key: 'priceMonitoring',
    label: 'Price monitoring',
    note: 'Collect retail listings for this brand and compare them against MAP.',
    adminOnly: false,
  },
  {
    key: 'autoDetectViolations',
    label: 'Auto-detect violations',
    note: 'Raise a violation as soon as a listing drops below the MAP floor.',
    adminOnly: false,
  },
  {
    key: 'alertRetailers',
    label: 'Alert retailers directly',
    note: 'Email the retailer when one of their listings breaches MAP.',
    adminOnly: true,
  },
  {
    key: 'weeklyDigest',
    label: 'Weekly digest',
    note: 'A Monday summary of violations, new listings and compliance drift.',
    adminOnly: false,
  },
];

export const BrandVendorsDashboardPage: React.FC = () => {
  const { brandId = '' } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const impersonation = useImpersonation();
  const [vendorView, setVendorView] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ['brand-directory-dashboard', brandId],
    queryFn: () => brandDirectoryApi.dashboard(brandId),
    enabled: Boolean(brandId),
  });

  // The ‹/› stepper walks the directory order, so it matches the table behind it.
  const { data: directory } = useQuery({
    queryKey: ['brand-directory'],
    queryFn: () => brandDirectoryApi.list(),
  });

  const siblings = useMemo(() => directory?.brands || [], [directory]);
  const index = siblings.findIndex((row) => row.id === brandId);

  const monitorMutation = useMutation({
    mutationFn: (patch: Partial<BrandMonitoringSettings>) => brandDirectoryApi.updateMonitoring(brandId, patch),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['brand-directory-dashboard', brandId] }),
  });

  if (isLoading) {
    return (
      <Box sx={{ display: 'grid', placeItems: 'center', py: '80px' }}>
        <CircularProgress size={22} />
      </Box>
    );
  }
  if (error || !data) {
    return <Alert severity="error" sx={{ m: 3 }}>{(error as Error)?.message || 'Dashboard unavailable'}</Alert>;
  }

  const brand = data.brand;
  const claimed = brand.claimedBy.status === 'claimed';
  const hasMapRules = data.mapRules.count > 0;
  const openViolations = data.violations.openCount;

  const monitoringPaused = !data.monitoring.priceMonitoring;

  const banner = !claimed
    ? {
        tone: bvColor.warningBar,
        title: 'No vendor org owns this brand yet',
        note: 'MAP rules and violation tracking stay empty until a rep claims the brand and an admin approves it.',
        action: { label: 'Open approvals', to: bvRoutes.approvals(brand.claimedBy.requestId) },
      }
    : monitoringPaused
      ? {
          // An empty violation list means nothing was checked, not that
          // everything complies — never report an all-clear from paused checks.
          tone: bvColor.warningBar,
          title: 'Price monitoring is paused for this brand',
          note: 'No retail listings are being checked, so an empty violations list says nothing about compliance.',
          action: null,
        }
      : !hasMapRules
        ? {
            tone: bvColor.warningBar,
            title: 'No MAP rules are published for this brand',
            note: 'Without a price floor there is nothing to measure retail listings against, so no violations can be raised.',
            action: null,
          }
        : openViolations > 0
          ? {
              tone: bvColor.danger,
              title: `${formatCount(openViolations)} open MAP violation${openViolations === 1 ? '' : 's'}`,
              note: `Across ${data.violations.retailerCount} retailer${data.violations.retailerCount === 1 ? '' : 's'} in the last ${data.violations.windowDays} days.`,
              action: null,
            }
          : {
              tone: bvColor.successBar,
              title: 'Every listing is within MAP',
              note: `No breaches across ${data.retailReach.carrying} carrying partner${data.retailReach.carrying === 1 ? '' : 's'} in the last ${data.violations.windowDays} days.`,
              action: null,
            };

  return (
    <Box sx={{ bgcolor: bvColor.page, minHeight: '100%' }}>
      {/* masthead */}
      <Box sx={{ bgcolor: bvColor.surface, borderBottom: `1px solid ${bvColor.border}`, px: '26px', pt: '20px', pb: '18px' }}>
        <Box sx={{ ...bvContentSx, display: 'flex', gap: '18px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <LogoFrame
            name={brand.name}
            url={brand.logoUrl}
            size={84}
            radius={bvRadius.logoFrameMedium}
            borderColor={bvColor.borderWarm}
            artSize={58}
            monogramSize={28}
            elevated
          />
          <Box sx={{ flex: 1, minWidth: 240 }}>
            <Box
              component="button"
              onClick={() => navigate(bvRoutes.brand(brand.id))}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '5px',
                p: 0,
                border: 'none',
                bgcolor: 'transparent',
                cursor: 'pointer',
                color: bvColor.ink4,
                ...bvType.caption,
                '&:hover': { color: bvColor.accent },
              }}
            >
              <IconChevron dir="left" size={12} /> Back to {brand.name} brand page
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '11px', mt: '5px', flexWrap: 'wrap' }}>
              <Box component="h1" sx={{ ...bvType.dashTitle, color: bvColor.ink, m: 0 }}>
                {brand.name}
              </Box>
              <Box
                component="span"
                sx={{
                  px: '9px',
                  py: '2px',
                  borderRadius: `${bvRadius.pill}px`,
                  border: `1px solid ${claimed ? bvColor.successBorder : bvColor.warningBorder}`,
                  color: claimed ? bvColor.success : bvColor.warning,
                  ...bvType.stagePill,
                }}
              >
                {claimed ? brand.claimedBy.organizationName : 'Unclaimed'}
              </Box>
              <Stepper
                prevLabel="Previous brand"
                nextLabel="Next brand"
                prevDisabled={index <= 0}
                nextDisabled={index < 0 || index >= siblings.length - 1}
                onPrev={() => index > 0 && navigate(bvRoutes.dashboard(siblings[index - 1].id))}
                onNext={() => index >= 0 && index < siblings.length - 1 && navigate(bvRoutes.dashboard(siblings[index + 1].id))}
              />
              {index >= 0 && (
                <Box sx={{ ...bvType.caption, color: bvColor.muted, ...tabular }}>
                  {index + 1} of {siblings.length}
                </Box>
              )}
            </Box>
            <Box sx={{ ...bvType.caption, color: bvColor.muted, mt: '6px' }}>
              {formatCount(data.catalog.itemCount)} products · {data.mapRules.count} MAP rules ·{' '}
              {data.retailReach.carrying} of {data.retailReach.totalStores} stores carrying
            </Box>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '9px' }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '9px',
                px: '11px',
                py: '7px',
                borderRadius: `${bvRadius.pill}px`,
                border: `1px solid ${vendorView ? bvColor.accent : bvColor.border}`,
                bgcolor: vendorView ? bvColor.surfaceTint : bvColor.surface,
                transition: bvTransition,
              }}
            >
              <Box sx={{ ...bvType.buttonSmall, color: vendorView ? bvColor.accentHover : bvColor.ink3 }}>
                View as the vendor
              </Box>
              <BvSwitch
                checked={vendorView}
                onChange={setVendorView}
                label="View this dashboard with the vendor's permissions"
                width={30}
                height={15}
              />
            </Box>
            {claimed && brand.claimedBy.organizationId && (
              <BvButton
                size="sm"
                onClick={() => {
                  impersonation.startImpersonationWithOrg({
                    id: brand.claimedBy.organizationId as string,
                    name: brand.claimedBy.organizationName || brand.name,
                    type: 'vendor',
                    status: 'active',
                  });
                  navigate('/app/dashboard');
                }}
              >
                Sign in as this vendor
              </BvButton>
            )}
          </Box>
        </Box>
      </Box>

      {/* body */}
      <Box sx={{ px: '26px', pt: '18px', pb: '40px' }}>
        <Box sx={{ ...bvContentSx, display: 'flex', flexDirection: 'column', gap: '14px' }}>
          {/* contextual banner */}
          <Box sx={{ ...bvCardSx, borderLeft: `3px solid ${banner.tone}`, display: 'flex', alignItems: 'center', gap: '14px', px: '16px', py: '13px' }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ ...bvType.cardTitle, color: bvColor.ink }}>{banner.title}</Box>
              <Box sx={{ ...bvType.caption, color: bvColor.muted, mt: '3px' }}>{banner.note}</Box>
            </Box>
            {banner.action && !vendorView && (
              <BvButton size="md" onClick={() => navigate(banner.action!.to)}>
                {banner.action.label}
              </BvButton>
            )}
          </Box>

          {/* tile row */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: 'repeat(auto-fit, minmax(180px, 1fr))',
                md: 'repeat(auto-fit, minmax(180px, 1fr))',
              },
              gap: '12px',
            }}
          >
            {[
              {
                label: 'Catalog',
                value: formatCount(data.catalog.itemCount),
                note: `${data.catalog.completePercent}% field completeness across the catalog.`,
                source: data.catalog.source,
                dot: data.catalog.itemCount > 0 ? bvColor.success : bvColor.danger,
              },
              {
                label: 'MAP rules',
                value: formatCount(data.mapRules.count),
                note: hasMapRules ? 'Price floors published for this brand.' : 'No floors published, so nothing to enforce.',
                source: data.mapRules.source,
                dot: hasMapRules ? bvColor.success : bvColor.warning,
              },
              {
                label: 'Open violations',
                value: formatCount(openViolations),
                note: `${data.violations.retailerCount} retailer${data.violations.retailerCount === 1 ? '' : 's'} in the last ${data.violations.windowDays} days.`,
                source: data.violations.source,
                dot: openViolations > 0 ? bvColor.danger : bvColor.success,
              },
              {
                label: 'Retail reach',
                value: `${data.retailReach.carrying}/${data.retailReach.totalStores}`,
                note: 'Stores carrying at least one product from this brand.',
                source: data.retailReach.source,
                dot: data.retailReach.carrying > 0 ? bvColor.success : bvColor.muted2,
              },
            ].map((tile) => (
              <Box key={tile.label} sx={{ ...bvCardSx, p: 0 }}>
                <Box sx={{ p: '13px' }}>
                  <Eyebrow>{tile.label}</Eyebrow>
                  <Box sx={{ ...bvType.bigStat, color: bvColor.ink, mt: '5px' }}>{tile.value}</Box>
                  <Box sx={{ ...bvType.caption, color: bvColor.muted, mt: '4px', minHeight: 32, lineHeight: 1.4 }}>
                    {tile.note}
                  </Box>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    px: '13px',
                    py: '9px',
                    borderTop: `1px solid ${bvColor.borderSubtle}`,
                  }}
                >
                  <StatusDot color={tile.dot} />
                  <Box sx={{ ...bvType.microCaption, color: bvColor.muted }}>{tile.source}</Box>
                </Box>
              </Box>
            ))}
          </Box>

          {/* violations + monitoring */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 360px' }, gap: '14px', alignItems: 'start' }}>
            <BvCard
              title="Price violations"
              caption={
                openViolations > 0
                  ? `${formatCount(openViolations)} open across ${data.violations.retailerCount} retailers`
                  : 'Nothing below the MAP floor right now'
              }
            >
              {data.violations.items.length === 0 ? (
                <BvEmptyState
                  headline={hasMapRules ? 'No violations to show' : 'No MAP rules, so no violations to show'}
                  note={
                    hasMapRules
                      ? 'Every tracked listing is at or above the published floor.'
                      : 'Publish a price floor and every retail listing gets measured against it from the next crawl.'
                  }
                  action={
                    // Setting MAP policy is the brand owner's call via an admin — a
                    // vendor viewing this page cannot publish floors themselves.
                    !hasMapRules && !vendorView ? (
                      <BvButton tone="primary" onClick={() => navigate('/app/admin/map-pricing')}>
                        Set provisional MAP rules
                      </BvButton>
                    ) : undefined
                  }
                  py="40px"
                />
              ) : (
                data.violations.items.map((item) => (
                  <Box
                    key={item.id}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '11px',
                      px: '16px',
                      py: '11px',
                      borderBottom: `1px solid ${bvColor.borderSubtle}`,
                      '&:last-of-type': { borderBottom: 'none' },
                    }}
                  >
                    <StatusDot color={bvColor.danger} size={7} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ ...bvType.tableSecondary, fontWeight: 500, color: bvColor.ink }}>
                        {item.retailerName}
                      </Box>
                      <Box sx={{ ...bvType.microCaption, color: bvColor.muted, mt: '2px' }}>
                        {formatCount(item.listingCount)} listing{item.listingCount === 1 ? '' : 's'} ·{' '}
                        {item.seenAt ? new Date(item.seenAt).toLocaleDateString('en-US') : 'recent'}
                      </Box>
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                      <Box sx={{ ...bvType.tableSecondary, fontWeight: 600, color: bvColor.danger, ...tabular }}>
                        {/* A normalized 0 is not a real gap — prefer a positive
                            amount, then the percentage, before showing nothing. */}
                        {item.underAmount != null && item.underAmount > 0
                          ? `$${item.underAmount.toFixed(2)}`
                          : item.underPercent != null && item.underPercent > 0
                            ? `${item.underPercent}%`
                            : '—'}
                      </Box>
                      <Box sx={{ ...bvType.microCaption, color: bvColor.muted }}>under</Box>
                    </Box>
                    {/* This only navigates; there is no retailer-notification
                        endpoint yet, so it must not promise one. */}
                    <BvButton size="xs" onClick={() => navigate(bvRoutes.organizations(item.retailerId))}>
                      Open org
                    </BvButton>
                  </Box>
                ))
              )}
            </BvCard>

            <BvCard
              title="Monitoring"
              action={
                <Box
                  component="span"
                  sx={{
                    px: '8px',
                    py: '2px',
                    borderRadius: `${bvRadius.pill}px`,
                    border: `1px solid ${data.monitoring.priceMonitoring ? bvColor.successBorder : bvColor.warningBorder}`,
                    color: data.monitoring.priceMonitoring ? bvColor.success : bvColor.warning,
                    ...bvType.stagePill,
                  }}
                >
                  {data.monitoring.priceMonitoring ? 'Active' : 'Paused'}
                </Box>
              }
            >
              {MONITORING_ROWS.map((option) => {
                const locked = option.adminOnly && vendorView;
                return (
                  <Box
                    key={option.key}
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '10px',
                      px: '13px',
                      py: '11px',
                      borderBottom: `1px solid ${bvColor.borderSubtle}`,
                      '&:last-of-type': { borderBottom: 'none' },
                    }}
                  >
                    <Box sx={{ pt: '2px' }}>
                      <BvSwitch
                        checked={Boolean(data.monitoring[option.key])}
                        disabled={locked || monitorMutation.isPending}
                        onChange={(next) => monitorMutation.mutate({ [option.key]: next })}
                        label={option.label}
                      />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
                        <Box sx={{ ...bvType.tableSecondary, fontWeight: 500, color: locked ? bvColor.muted : bvColor.ink }}>
                          {option.label}
                        </Box>
                        {option.adminOnly && (
                          <Box
                            component="span"
                            sx={{
                              px: '6px',
                              py: '1px',
                              borderRadius: `${bvRadius.chip}px`,
                              bgcolor: bvColor.borderSubtle,
                              color: bvColor.muted,
                              ...bvType.microCaption,
                            }}
                          >
                            admin only
                          </Box>
                        )}
                      </Box>
                      <Box sx={{ ...bvType.microCaption, color: bvColor.muted, mt: '3px', lineHeight: 1.45 }}>
                        {option.note}
                      </Box>
                    </Box>
                  </Box>
                );
              })}
              <Box sx={{ px: '13px', py: '11px', bgcolor: bvColor.page, borderTop: `1px solid ${bvColor.borderSubtle}`, ...bvType.microCaption, color: bvColor.muted, lineHeight: 1.45 }}>
                Retailer alerts reach third parties on the brand's behalf, so they stay with admins even when the vendor is signed in.
              </Box>
            </BvCard>
          </Box>

          {/* retailer performance */}
          <BvCard
            title="Retailer performance"
            caption={`${data.retailerPerformance.length} partner${data.retailerPerformance.length === 1 ? '' : 's'} carrying ${brand.name}`}
            action={
              <Box sx={{ ...bvType.caption, color: bvColor.muted, ...tabular }}>
                {formatCount(data.retailerPerformance.reduce((total, row) => total + row.listings, 0))} listings
              </Box>
            }
          >
            {data.retailerPerformance.length === 0 ? (
              <BvEmptyState
                headline="No retail partner carries this brand"
                note="Once a store's catalog includes products from this brand it appears here with its MAP compliance."
                py="40px"
              />
            ) : (
              <>
                <Box
                  sx={{
                    display: { xs: 'none', md: 'grid' },
                    gridTemplateColumns: { xs: '1fr', md: GRID },
                    columnGap: '12px',
                    px: '16px',
                    py: '9px',
                    bgcolor: bvColor.page,
                    borderBottom: `1px solid ${bvColor.border}`,
                    ...bvType.caption,
                    color: bvColor.muted,
                  }}
                >
                  <Box>Retail partner</Box>
                  <Box>MAP compliance</Box>
                  <Box sx={{ textAlign: 'right' }}>Listings</Box>
                  <Box sx={{ textAlign: 'right' }}>Violations</Box>
                  <Box />
                </Box>
                {data.retailerPerformance.map((row) => {
                  const compliance = COMPLIANCE[row.compliance];
                  return (
                    <Box
                      key={row.organizationId}
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', md: GRID },
                        columnGap: '12px',
                        rowGap: '8px',
                        alignItems: 'center',
                        px: '16px',
                        py: '11px',
                        borderBottom: `1px solid ${bvColor.borderSubtle}`,
                        '&:last-of-type': { borderBottom: 'none' },
                      }}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Box sx={{ ...bvType.tablePrimary, color: bvColor.ink }}>{row.organizationName}</Box>
                        <Box sx={{ ...bvType.microCaption, color: bvColor.muted, mt: '2px' }}>Retail partner</Box>
                      </Box>
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
                          <Box sx={{ ...bvType.tableSecondary, color: compliance.ink, fontWeight: 500 }}>
                            {compliance.label}
                          </Box>
                        </Box>
                        <ProgressBar percent={compliance.percent} tone={compliance.tone} height={4} sx={{ mt: '5px' }} />
                      </Box>
                      <Box sx={{ textAlign: 'right', ...bvType.tableSecondary, color: bvColor.ink3, ...tabular }}>
                        {formatCount(row.listings)}
                      </Box>
                      <Box
                        sx={{
                          textAlign: 'right',
                          ...bvType.tableSecondary,
                          color: row.violationCount > 0 ? bvColor.danger : bvColor.ink3,
                          ...tabular,
                        }}
                      >
                        {formatCount(row.violationCount)}
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <BvButton size="xs" onClick={() => navigate(bvRoutes.organizations(row.organizationId))}>
                          Open org
                        </BvButton>
                      </Box>
                    </Box>
                  );
                })}
              </>
            )}
          </BvCard>
        </Box>
      </Box>
    </Box>
  );
};

export default BrandVendorsDashboardPage;
